// @ts-nocheck

import { MinMaxValue } from "../../types/filters";

// ProductAttributeId
export type ProductAttributeId = number;

// IndustryParams
export interface IndustryParams {
  industryIds?: number[] | null;
  excludedIndustryIds?: number[] | null;
  naics2012Ids?: string[] | null;
  excludedNaics2012Ids?: string[] | null;
  sicUS1987Ids?: string[] | null;
  excludedSicUS1987Ids?: string[] | null;
}

// InclusionMethod
export const InclusionMethod = {
  ALL_PRESENT: "ALL_PRESENT",
  ANY_PRESENT: "ANY_PRESENT",
  NONE_PRESENT: "NONE_PRESENT",
} as const;
export type InclusionMethodValue =
  (typeof InclusionMethod)[keyof typeof InclusionMethod];

// InclusionFilter
export interface InclusionFilter {
  ids: number[];
  roleIds?: string[] | null;
  departmentIds?: string[] | null;
  inclusionMethod?: InclusionMethodValue | null;
  isBulk?: boolean | null;
  installAge?: MinMaxValue | null;
  installAgeMonths?: MinMaxValue | null;
  intensity?: MinMaxValue | null;
  type?: string | null;
  collectionId?: string | null;
  uploadedFileName?: string | null;
  uploadedItems?: { id: number; name: string }[] | null;
}

// TopicsInclusionFilter
export interface TopicsInclusionFilter {
  ids: string[];
  inclusionMethod?: InclusionMethodValue;
  collectionIds?: string[];
}

// IntentFilter
export interface IntentFilter {
  signalScore?: string | null;
  buyersJourneyIds?: string[];
  location?: {
    countryAlpha2s?: string[] | null;
    excludedCountryAlpha2s?: string[] | null;
    regionNames?: string[] | null;
    stateIds?: string[] | null;
  };
  contextTypeIds?: string[];
  numberOfCadences?: number | null;
  topics?: TopicsInclusionFilter;
}

// CompanyMatchFilter
export interface CompanyMatchFilter {
  fileId: string;
  userEmail?: string;
  fileName?: string;
  uploaded?: string;
  lastMatched?: string | null;
  totalRows?: number | null;
  processedRows?: number | null;
  matchedRows?: number | null;
  invalidRows?: number | null;
  salesWins?: number | null;
  salesLosses?: number | null;
  processedChunks?: number | null;
  totalChunks?: number | null;
  filterMethod?: string | null;
  includeHierarchy?: boolean | null;
  isPlatformPermissible?: boolean | null;
  isV1Dataset?: boolean | null;
  rgifExclusiveCompany?: boolean | null;
  /** Overseer org id — covered-wagon mints its rematch token from it. */
  orgId?: string;
}

// WhitespaceType
export enum WhitespaceType {
  NONE = "NONE",
  MATCH_FILE = "MATCH_FILE",
}

// WhitespaceFilter
export interface WhitespaceFilter {
  type: WhitespaceType;
}

// OpportunityType
export enum OpportunityType {
  CUSTOMER = "CUSTOMER",
  WHITESPACE = "WHITESPACE",
  PROSPECT_WHITESPACE = "PROSPECT_WHITESPACE",
  EXPANSION_WHITESPACE = "EXPANSION_WHITESPACE",
}

// OpportunityTypeFilter
export interface OpportunityTypeFilter {
  type: OpportunityType;
}

// SpendFilter
export interface SpendFilter {
  categoryId?: string | null;
  min?: number | null;
  max?: number | null;
}
export type SpendFilterValueType = SpendFilter[];

// VendorPenetrationFilter
export interface VendorPenetrationFilter extends InclusionFilter {
  productCount?: MinMaxValue;
}

// ProductCategoryFilter
export interface ProductCategoryFilter {
  ids: string[];
  roleIds?: string[] | null;
  departmentIds?: string[] | null;
  installAge?: MinMaxValue | null;
  installAgeMonths?: MinMaxValue | null;
}

// ProductAttributeFilter
export interface ProductAttributeFilter {
  ids: number[];
  roleIds?: string[] | null;
  departmentIds?: string[] | null;
  installAge?: MinMaxValue | null;
  installAgeMonths?: MinMaxValue | null;
}

// ContactsFilter
export interface ContactsFilter {
  /** UI-only: never sent; decides whether the criteria also narrow companies. */
  mode?: "filter_company_list" | "export_contacts_only";
  jobSeniority?: { include?: string[]; exclude?: string[] };
  jobFunctions?: { include?: string[]; exclude?: string[] };
  jobSubFunctions?: { include?: string[]; exclude?: string[] };
  jobTitleKeywords?: {
    include?: string[];
    exclude?: string[];
    inclusionMethod?: "ANY_PRESENT" | "ALL_PRESENT";
  };
  location?: {
    countryAlpha2s?: string[];
    regionNames?: string[];
    stateIds?: string[];
    excludedCountryAlpha2s?: string[];
  };
  timeInRoleBuckets?: string[];
  primaryCompanyOnly?: boolean;
}

// CompanyFilter
export interface CompanyFilter {
  productAttributeIds?: ProductAttributeId[];
  productAttributes?: ProductAttributeFilter;
  companyMatch?: CompanyMatchFilter;
  opportunityType?: OpportunityTypeFilter; // Updated to use the new structure
  hgIds?: string[];
  hgIdsIncludingCorporateRelatives?: string[];
  fuzzySearch?: string;
  countryAlpha2s?: string[];
  regionNames?: string[];
  stateIds?: string[];
  excludedCountryAlpha2s?: string[];
  globalPresence?: {
    isMultiNational: boolean;
    hqCountryCodes?: string[] | null;
    hqRegions?: string[] | null;
    subsidiaryLocations?: {
      countryCodes?: string[] | null;
      regions?: string[] | null;
      inclusionMethod: InclusionMethodValue;
      countMatching?: number | null;
    };
    subCountryCodes?: string[] | null;
    subRegions?: string[] | null;
  };
  isGu?: boolean;
  isDu?: boolean;
  isGhq?: boolean;
  isGp?: boolean;
  isDp?: boolean;
  industry?: IndustryParams;
  employees?: MinMaxValue;
  hasFixedEmployees?: boolean;
  revenue?: MinMaxValue;
  hasFixedRevenue?: boolean;
  intent?: IntentFilter;
  fitScore?: MinMaxValue;
  intentScore?: MinMaxValue;
  productCategoryIds?: string[];
  productCategories?: ProductCategoryFilter;
  vendors?: VendorPenetrationFilter[];
  products?: InclusionFilter[];
  spend?: SpendFilterValueType;
  whitespace?: WhitespaceFilter;
  roleIds?: string[];
  departmentIds?: string[];
  includeDecisionMakersAndInfluencersOnly?: boolean;
  contactFilters?: ContactsFilter;
  buyerIntentProducts?: Array<{
    ids: number[];
    inclusionMethod?: InclusionMethodValue;
  }>;
  buyerIntentVendors?: Array<{
    ids: number[];
    inclusionMethod?: InclusionMethodValue;
  }>;
  buyerIntentCategories?: { ids: string[] };
  buyerIntentAttributes?: { ids: number[] };
}

// TODO HIP-6393 - Why are so many of these default values null, which aren't accepted types?
// Comes from `translateOppGenFiltersToCompanyFilter`
export const DEFAULT_COMPANY_FILTER: CompanyFilter = {
  companyMatch: null,
  countryAlpha2s: null,
  employees: undefined,
  excludedCountryAlpha2s: null,
  fitScore: undefined,
  fuzzySearch: null,
  hgIds: null,
  globalPresence: {
    isMultiNational: false,
    subsidiaryLocations: {
      inclusionMethod: InclusionMethod.ANY,
    },
  },
  hgIdsIncludingCorporateRelatives: null,
  industry: null,
  intent: {
    buyersJourneyIds: [],
    contextTypeIds: [],
    location: {
      countryAlpha2s: null,
      excludedCountryAlpha2s: null,
      regionNames: null,
      stateIds: null,
    },
    numberOfCadences: 4,
    signalScore: "HIGH",
    topics: {
      ids: [],
      inclusionMethod: "ANY_PRESENT",
    },
  },
  intentScore: undefined,
  isDu: null,
  isGu: null,
  isGhq: null,
  isGp: null,
  isDp: null,
  productAttributes: {
    ids: [],
    installAge: null,
    installAgeMonths: null,
  },
  productCategories: {
    departmentIds: [],
    ids: [],
    roleIds: [],
    installAge: null,
    installAgeMonths: null,
  },
  products: [
    {
      departmentIds: [],
      ids: [],
      inclusionMethod: "ANY_PRESENT",
      roleIds: [],
      installAge: null,
      installAgeMonths: null,
    },
  ],
  regionNames: null,
  revenue: undefined,
  spend: undefined,
  stateIds: null,
  vendors: [
    {
      departmentIds: [],
      ids: [],
      inclusionMethod: "ANY_PRESENT",
      roleIds: [],
      installAge: null,
      installAgeMonths: null,
    },
  ],
  whitespace: null,
  opportunityType: undefined,
  departmentIds: undefined,
  roleIds: undefined,
  includeDecisionMakersAndInfluencersOnly: undefined,
};

export interface EntityTypeFilterValue {
  isGu?: boolean;
  isDu?: boolean;
  isGhq?: boolean;
  isGp?: boolean;
  isDp?: boolean;
}
